// MachinesManagePage.jsx
import React from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as DeleteOutlineIcon } from "../../assets/DeleteSvg.svg";
import { ReactComponent as BackSvg } from "../../assets/BackSvg.svg";

import { Add } from "@mui/icons-material";
import {
  useForm,
  FormProvider,
  useFieldArray,
  Controller,
  useWatch,
} from "react-hook-form";
import { ReactComponent as EquipmentPhotoWrapper } from "../../assets/EquipmentPhotoWrapper.svg";
import { ReactComponent as UptimeSvg } from "../../assets/uptimeSvg.svg";
import QRRCode from "react-qr-code";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { ReactComponent as DeltaTechSvg } from "../../assets/DeltaTech.svg";
import GoBack from "../../components/GoBack";

/* =========================
   Dummies (swap to API)
========================= */
const PRODUCT_TYPES = [
  "MachineBuilder RM 40K",
  "MachineBuilder RM 60K",
  "CNC Pro 3000",
  "LaserCut X2",
];

const YEARS = Array.from({ length: 40 }, (_, i) => `${new Date().getFullYear() - i}`);

const DUMMY_LOCATIONS = [
  { name: "Werk Pforzheim", bereiche: [{ name: "Halle A" }, { name: "Halle B" }] },
  { name: "Werk Győr", bereiche: [{ name: "Linie 1" }, { name: "Linie 2" }] },
];

/* =========================
   Helpers
========================= */
const makeMachine = () => ({
  productType: "",
  year: "",
  locationArea: "",
  locationIndex: "",
  bereichName: "",
  serialNumber: "",
  softwareName: "",
  softwareVersion: "",
  controlType: "",
  controlManufacturer: "",
});

const buildLocationOptions = (locations) => {
  const opts = [];
  locations.forEach((loc, idx) => {
    const locName = loc?.name || `Standort ${idx + 1}`;
    opts.push({
      value: JSON.stringify({ locationIndex: idx, bereichName: "" }),
      label: `${locName} — (kein Bereich)`,
    });
    (loc?.bereiche || []).forEach((b) => {
      if (b?.name) {
        opts.push({
          value: JSON.stringify({ locationIndex: idx, bereichName: b.name }),
          label: `${locName} — ${b.name}`,
        });
      }
    });
  });
  return opts;
};

/* =========================
   Page Component
========================= */
const MachinesManagePage = () => {
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      customerName: "DELTA Tech",
      locations: DUMMY_LOCATIONS,
      machines: [
        {
          productType: "MachineBuilder RM 40K",
          year: "2021",
          serialNumber: "AKP-59F-3KP",
          locationArea: JSON.stringify({ locationIndex: 0, bereichName: "" }),
          locationIndex: 0,
          bereichName: "",
          softwareName: "MBuilderSoft",
          softwareVersion: "1.08.11",
          controlType: "Control X-2",
          controlManufacturer: "MBuilder",
        },
        {
          productType: "CNC Pro 3000",
          year: "2020",
          serialNumber: "CNC-2020-77X",
          locationArea: JSON.stringify({ locationIndex: 1, bereichName: "Linie 2" }),
          locationIndex: 1,
          bereichName: "Linie 2",
          softwareName: "ProCNC OS",
          softwareVersion: "3.4.2",
          controlType: "Siemens 840D",
          controlManufacturer: "Siemens",
        },
      ],
    },
  });

  const { control, handleSubmit, setValue, getValues } = methods;

  const { fields, append, remove } = useFieldArray({ control, name: "machines" });

  const locations = useWatch({ control, name: "locations" });
  const locationOptions = React.useMemo(
    () => buildLocationOptions(locations || []),
    [locations]
  );

  const [expanded, setExpanded] = React.useState(0);

  const onSubmit = (values) => {
    console.log("SAVE payload:", values);
  };

  const current = useWatch({ control, name: `machines.${expanded}` });
  const currentValid = React.useMemo(() => {
    if (!current) return false;
    const { productType, year, serialNumber, locationArea } = current;
    return Boolean(productType && year && serialNumber && locationArea);
  }, [current]);

  return (
    <Box sx={{ p: 2 }} className="editKundenMachinesPage">
      <GoBack  icon={<BackSvg />} text={"Kunde DELTA Tech"} color="rgba(0, 0, 0, 0.50)" to={"/kunden-detail"}/>
      <br/>
      <br/>

      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: 2 }}>
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Typography className="textSecondary" sx={{ fontWeight: 500, mb: 2 }}>
            <DeltaTechSvg /> {"Company Name"}
          </Typography>

          <Typography variant="h6" className="fRubik" sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
            Maschinen verwalten
          </Typography>
          <Typography className="textSecondary" sx={{ fontWeight: 500, marginTop: 1.5 }}>
            Maschinen basieren auf den von Ihnen angelegten{" "}
            <span className="cp" style={{ color: "#38748D" }}>
              Produkttypen.
            </span>
          </Typography>
        </Box>

        <FormProvider {...methods}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ p: 3 }}>
            {fields.map((f, idx) => (
              <Accordion
                key={f.id}
                expanded={expanded === idx}
                onChange={(_, open) => setExpanded(open ? idx : -1)}
                disableGutters
                sx={{
                  mb: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Typography sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
                      {getValues(`machines.${idx}.productType`)?.trim() || "Neue Maschine"}
                    </Typography>
                    <IconButton
                      aria-label="Entfernen"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(idx);
                        if (fields.length - 1 <= 1) setExpanded(0);
                        else setExpanded((prev) => Math.max(0, prev - 1));
                      }}
                    >
                      <DeleteOutlineIcon color="error" />
                    </IconButton>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ p: "12px 168px 16px 16px" }}>
                  <Grid container spacing={4}>
                    {/* Produkttyp */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.productType`}
                        control={control}
                        rules={{ required: "Pflichtfeld" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Produkttyp"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          >
                            {PRODUCT_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    {/* Baujahr */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.year`}
                        control={control}
                        rules={{ required: "Pflichtfeld" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Baujahr"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          >
                            {YEARS.map((y) => (
                              <MenuItem key={y} value={y}>
                                {y}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    {/* Standort & Bereich */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.locationArea`}
                        control={control}
                        rules={{ required: "Pflichtfeld" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Standort und Bereich"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            onChange={(e) => {
                              const val = e.target.value;
                              field.onChange(val);
                              try {
                                const parsed = JSON.parse(val);
                                setValue(`machines.${idx}.locationIndex`, parsed.locationIndex, {
                                  shouldDirty: true,
                                });
                                setValue(
                                  `machines.${idx}.bereichName`,
                                  parsed.bereichName || "",
                                  { shouldDirty: true }
                                );
                              } catch {
                                setValue(`machines.${idx}.locationIndex`, "", { shouldDirty: true });
                                setValue(`machines.${idx}.bereichName`, "", { shouldDirty: true });
                              }
                            }}
                          >
                            {locationOptions.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    {/* Seriennummer */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.serialNumber`}
                        control={control}
                        rules={{ required: "Pflichtfeld" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Hersteller-Seriennummer"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    {/* Optional fields */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.softwareName`}
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Software-Bezeichnung" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.softwareVersion`}
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Software-Version" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.controlType`}
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Steuerungstyp" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`machines.${idx}.controlManufacturer`}
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Steuerungshersteller" />
                        )}
                      />
                    </Grid>

                    <div className="stickerWrapper" style={{ width: "100%", padding: "36px 0px 24px 36px" }}>
                      <div style={{ width: "50%" }}>
                        <div className="_title">Vorschau des Equipments</div>
                        <div className="equipmentPlateDiv bxShadow" style={{ width: "100%" }}>
                          <div className="photoWrapper">
                            <EquipmentPhotoWrapper />
                          </div>
                          <div className="_contentWrapper">
                            <div className="_border"></div>

                            <div className="_content" style={{ padding: "16px 0px 0px 0px" }}>
                              <Tooltip title={"internerName"}>
                                <div className="eqName1 truncate" style={{ paddingLeft: "16px" }}>
                                  internerName
                                </div>
                              </Tooltip>

                              <div className="eqName2" style={{ paddingLeft: "16px" }}>
                                text1
                              </div>
                              <div className="eqSiteName" style={{ paddingLeft: "16px" }}>
                                text2
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{ width: "50%", marginLeft: "4%" }}>
                        <div className="_title">Service-Sticker</div>
                        <div className="equipmentPlateDiv bxShadow" style={{ width: "100%" }}>
                          <div className="qrCodeWrapper">
                            <QRRCode
                              size={256}
                              style={{ height: "100%", maxWidth: "100%", width: "100%" }}
                              value={"testing"}
                              viewBox={`0 0 256 256`}
                            />
                          </div>
                          <div className="_contentWrapper">
                            <div className="_content" style={{ padding: "16px 0px 0px 0px" }}>
                              <div className="eqName1">
                                <UptimeSvg />
                              </div>
                              <div className="qrTitle1">Hersteller : </div>
                              <div className="qrtitle2">Seriennr:</div>
                            </div>
                          </div>
                        </div>
                        <div className="downloadAsPDF">
                          <PDFDownloadLink document={<div></div>} fileName="-QRCode.pdf">
                            {({ loading }) => (loading ? "Loading document..." : "Download as PDF")}
                          </PDFDownloadLink>
                        </div>
                      </div>
                    </div>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}

            <Button
              variant="outlined"
              startIcon={<Add />}
              disabled={fields.length > 0 && !currentValid}
              onClick={() => {
                const next = fields.length;
                append(makeMachine());
                setExpanded(next);
              }}
              sx={{ mt: 4 }}
            >
              Maschine hinzufügen
            </Button>
          </Box>
        </FormProvider>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          type="submit"
          variant="contained"
          onClick={methods.handleSubmit(onSubmit)}
          sx={{ bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "#0b3140" } }}
        >
          ÄNDERUNGEN SPEICHERN
        </Button>
      </Stack>
    </Box>
  );
};

export default MachinesManagePage;
